import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { firstOrDefault, flatMapSuperDeep } from "@ankaadia/ankaadia-shared";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { Document, DocumentMode, StaticDataType } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType, FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { compact, isEmpty, isNil, pick, sumBy } from "lodash";
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from "primeng/accordion";
import { MenuItem } from "primeng/api";
import { Menu } from "primeng/menu";
import { debounceTime, map, merge, Observable, of, switchMap } from "rxjs";
import { environment } from "../../../environments/environment";
import { DocumentsService } from "../../features/documents/documents.service";
import { CandidateProfileDialogService } from "../../organization-specific/base-candidate-profile/candidate-profile-dialog.service";
import { SimpleMenuBarItem } from "../../shared/components/simple-menu-bar/simple-menu-bar.component";
import { LocalStorageService } from "../../shared/services/local-storage.service";
import { StaticDataService } from "../../shared/static-data/static-data.service";

@Component({
  selector: "new-ui-formly-menu-bar",
  templateUrl: "./new-ui-formly-menu-bar.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, Accordion, AccordionPanel, AccordionHeader, AccordionContent, NewUiButton, Menu],
})
export class NewUiFormlyMenuBarComponent extends FieldType implements OnInit {
  private readonly language = this.transloco.getActiveLang();

  protected readonly storageKey = "remembered-menu-item";
  protected readonly isNil = isNil;

  protected invalidity: number[];
  protected uploadMenu: MenuItem[];
  protected items: SimpleMenuBarItem<FormlyFieldConfig>[] = [];
  protected selectedItem: SimpleMenuBarItem<FormlyFieldConfig> | null = null;

  get totalErrors(): number {
    return sumBy(this.items ?? [], (item) => Number(item.badge ?? 0));
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly storage: LocalStorageService,
    private readonly staticDataService: StaticDataService,
    private readonly transloco: TranslocoService,
    private readonly documentService: DocumentsService,
    private readonly profileDialogService: CandidateProfileDialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setUploadMenu();
    this.updateMenuItems();
    if (!environment.newUI) {
      merge(this.options.fieldChanges, this.form.statusChanges)
        .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
        .subscribe(() => this.updateMenuItems());
    }
  }

  protected openUploadDialog(documentType: string): void {
    this.documentService
      .getByType(documentType, this.formState.organizationId, this.formState.candidateId)
      .pipe(
        switchMap((document) => (document ? of(document) : this.createDocument(documentType))),
        map((document) => ({
          document,
          candidateForm: this.form,
          showDocumentCompletion: this.props.showDocumentCompletion,
        }))
      )
      .subscribe((event) => this.profileDialogService.documentDialog.next(event));
  }

  protected setSelectedItem(item: SimpleMenuBarItem<FormlyFieldConfig> | null): void {
    this.selectedItem = item;
    this.storage.set(this.storageKey, item.data?.id);
  }

  private createDocument(type: string): Observable<Document> {
    return this.documentService.createEmptyDocument(
      DocumentMode.Candidate,
      this.formState.organizationId,
      this.formState.candidateId,
      null,
      type
    );
  }

  private setUploadMenu(): void {
    if (!this.props.uploadTypes) return;

    if (this.formState.candidateId) {
      this.staticDataService
        .getStaticData(
          StaticDataType.AllowedUploadFileTypes,
          this.language,
          pick(this.formState, "candidateId", "organizationId")
        )
        .subscribe((data) => {
          this.uploadMenu = this.props.uploadTypes?.map((documentType) => ({
            label: data.find((d) => d.value == documentType)?.label,
            command: (): void => this.openUploadDialog(documentType),
          }));
          this.changeDetector.markForCheck();
        });
    } else {
      this.uploadMenu = null;
    }
  }

  private errorCount(field: FormlyFieldConfig): number {
    return field.fieldGroup?.length && !field.fieldArray
      ? sumBy(field.fieldGroup, (f) => this.errorCount(f))
      : field.formControl.invalid && (!!field.formControl.errors || this.hasNestedErrors(field))
        ? 1
        : 0;
  }

  // errors are not always propagated to the parent field, hence we also check the nested fields for errors
  private hasNestedErrors(field: FormlyFieldConfig): boolean {
    const nestedErrors = flatMapSuperDeep(field?.fieldGroup, (x) => x?.fieldGroup).map((x) => x?.formControl?.errors);
    return !isEmpty(compact(nestedErrors));
  }

  private updateMenuItems(): void {
    const tabs = this.field.fieldGroup ?? [];
    this.items = tabs.map((tab) => {
      const errorCount = this.errorCount(tab);
      return {
        id: tab.id,
        label: tab.props.label,
        data: tab,
        badge: errorCount === 0 ? null : errorCount.toString(),
      };
    });

    if (isNil(this.selectedItem)) {
      const selectedTabKey = this.storage.get(this.storageKey);
      this.setSelectedItem(this.findSelectedItem(selectedTabKey));
    }

    this.changeDetector.markForCheck();
  }

  private findSelectedItem(selectedTabKey: string | number): SimpleMenuBarItem<FormlyFieldConfig> | null {
    const items = this.items ?? [];
    const firstTab = firstOrDefault(items);
    return !isNil(selectedTabKey) ? (firstOrDefault(items, (t) => t.id === selectedTabKey) ?? firstTab) : firstTab;
  }
}
